'use client'

import { useEffect, useState } from 'react'
import { logClientError } from '@/lib/logError'
import { isChunkLoadError, reloadOnceForChunkError } from '@/lib/chunkError'
import { isTransientFetchError, clientDiagnostics } from '@/lib/clientErrors'

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const chunkError = isChunkLoadError(error)
  // A dropped connection is not a crash, and saying "an unexpected error occurred" to someone
  // whose phone just lost signal is both wrong and alarming — a real invitee saw exactly this
  // five minutes into his first session (NEA-233). reset() genuinely recovers here, unlike the
  // chunk case, so the recovery was never the problem; the wording was.
  //
  // ⚠️ Chunk takes precedence. A stale chunk can surface as a fetch failure too, and it needs
  // the reload — reset() can't fix it, because the chunk is genuinely gone.
  const offline = !chunkError && isTransientFetchError(error)
  // A stale-chunk error after a deploy self-heals with a one-time reload (fresh HTML +
  // current chunks).
  const [recovering, setRecovering] = useState(false)

  useEffect(() => {
    logClientError({
      source: 'error_boundary',
      message: error.message,
      stack: error.stack,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      metadata: { digest: error.digest, chunk: chunkError, ...clientDiagnostics() },
    })
    if (chunkError && reloadOnceForChunkError()) setRecovering(true)
  }, [error, chunkError])

  if (recovering) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-2 p-8 text-center">
        <p className="text-sm text-gray-500">Updating to the latest version…</p>
      </div>
    )
  }

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-8 text-center">
      <h2 className="text-xl font-semibold text-gray-900">
        {offline ? 'Couldn’t reach ppleague' : 'Something went wrong'}
      </h2>
      <p className="text-sm text-gray-500">
        {offline
          ? 'The connection dropped before the page finished loading. Check your signal and try again — nothing was lost.'
          : 'An unexpected error occurred. The issue has been logged.'}
      </p>
      <button
        onClick={reset}
        className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
      >
        Try again
      </button>
    </div>
  )
}
